import React, { useState, useEffect } from 'react';
import { User, Meeting } from '../types/User';
import { Calendar, Clock, MapPin, User as UserIcon, X, Check } from 'lucide-react';
import { MeetingService } from '../services/meetingService';
import CustomNotification from './CustomNotification';

interface MyCalendarProps {
  currentUser: User;
  refreshTrigger?: number;
  onMeetingUpdate?: () => void;
}

const MyCalendar: React.FC<MyCalendarProps> = ({ currentUser, refreshTrigger, onMeetingUpdate }) => {
  const [selectedDate, setSelectedDate] = useState('2026-10-06');
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ show: false, message: '', type: 'info' });

  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    // OPTIMIZATION: Only load if not already loaded or refreshTrigger changed
    if (!dataLoaded || refreshTrigger > 0) {
      loadMeetings();
    }
  }, [currentUser.id, refreshTrigger, dataLoaded]);

  const loadMeetings = async () => {
    console.log("🔄 LOAD MEETINGS CALLED");
    setLoading(true);
    
    // Show loader only after 2 seconds
    const loaderTimeout = setTimeout(() => {
      setShowLoader(true);
    }, 2000);
    
    try {
      const userMeetings = await MeetingService.getUserMeetings(currentUser.id);
      console.log("🔄 SET MEETINGS:", userMeetings.length, "meetings");
      setMeetings(userMeetings);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      clearTimeout(loaderTimeout);
      setLoading(false);
      setShowLoader(false);
    }
  };

  const handleCancelMeeting = async (meetingId: string) => {
    const meetingToCancel = meetings.find(m => m.id === meetingId);
    const otherParty = meetingToCancel?.requester_id === currentUser.id 
      ? meetingToCancel?.recipient.name 
      : meetingToCancel?.requester.name;
      
    console.log("🗑️ DELETING MEETING:", meetingId); const result = await MeetingService.deleteMeeting(meetingId, currentUser.id); console.log("🗑️ DELETE RESULT:", result);
    if (result.success) {
      setNotification({
        show: true,
        message: `Meeting with ${otherParty} has been cancelled. They will be notified.`,
        type: 'info'
      });
      // Force refresh the meetings list
      console.log("🔄 REFRESHING MEETINGS LIST..."); await loadMeetings(); console.log("🔄 MEETINGS LIST REFRESHED");
      // Notify parent component about the update
      onMeetingUpdate?.();
    } else {
      setNotification({
        show: true,
        message: 'Failed to cancel meeting: ' + result.error,
        type: 'error'
      });
    }
  };

  const handleAcceptMeeting = async (meetingId: string) => {
    if (window.confirm("Are you sure you want to accept this meeting?")) {
      const result = await MeetingService.updateMeetingStatus(meetingId, 'confirmed');
      if (result.success) {
        setNotification({
          show: true,
          message: "Meeting accepted successfully!",
          type: "success"
        });
        await loadMeetings();
        onMeetingUpdate?.();
      } else {
        setNotification({
          show: true,
          message: "Failed to accept meeting: " + result.error,
          type: "error"
        });
      }
    }
  };

  const handleRejectMeeting = async (meetingId: string) => {
    if (window.confirm("Are you sure you want to reject this meeting?")) {
      const result = await MeetingService.updateMeetingStatus(meetingId, 'rejected');
      if (result.success) {
        setNotification({
          show: true,
          message: "Meeting rejected and slot freed up successfully!",
          type: "success"
        });
        await loadMeetings();
        onMeetingUpdate?.();
      } else {
        setNotification({
          show: true,
          message: "Failed to reject meeting: " + result.error,
          type: "error"
        });
      }
    }
  };

  const todaysMeetings = meetings.filter(meeting => meeting.meeting_date === selectedDate);
  
  // Filter out cancelled meetings
  const activeMeetings = todaysMeetings.filter(meeting => meeting.status !== 'cancelled');

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">My Calendar</h2>
        <p className="text-gray-600 break-words">LOGISTICS MARKET EXPO & CONFERENCE 2026 — 6 October 2026, Beogradski sajam, Hala 5</p>
      </div>

      {/* Date Selector */}
      <div className="mb-6">
        <div className="inline-flex items-center space-x-2 bg-white rounded-lg border p-1">
          <button className="px-4 py-2 rounded-md text-white font-medium" style={{ backgroundColor: '#C89116' }}>
            Oct 6
          </button>
          <span className="text-gray-500 px-2">Conference Day</span>
        </div>
      </div>

      {/* Meetings Timeline */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <Calendar className="w-5 h-5 mr-2" style={{ color: '#C89116' }} />
            Scheduled Meetings - B2B Sessions (13:30 - 15:30)
          </h3>
        </div>
        
        <div className="p-4">
          {showLoader ? (
            <div className="text-center py-8 text-gray-500">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                <div>Loading meetings...</div>
              </div>
            </div>
          ) : activeMeetings.length > 0 ? (
            <div className="space-y-4">
              {activeMeetings.map((meeting) => (
                <div key={meeting.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between relative">
                    <div className="flex-1 pr-2 lg:pr-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="flex items-center" style={{ color: '#C89116' }}>
                          <Clock className="w-4 h-4 mr-1" />
                          <span className="font-semibold">{meeting.time_slot}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>Table {meeting.table_number}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          meeting.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800'
                            : meeting.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                        </span>
                      </div>
                      
                      <h4 className="font-semibold text-gray-800 mb-2">{meeting.subject}</h4>
                      
                      <div className="flex items-center space-x-4 mb-2">
                        <div className="flex items-center">
                          <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            Meeting with {meeting.requester_id === currentUser.id ? meeting.recipient.name : meeting.requester.name}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {meeting.requester_id === currentUser.id ? meeting.recipient.company : meeting.requester.company}
                      </p>
                      
                      {meeting.description && (
                        <p className="text-sm text-gray-500 italic">{meeting.description}</p>
                      )}
                    </div>
                    
                    <div className="ml-4 hidden lg:block">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        {(meeting.requester_id === currentUser.id ? meeting.recipient.avatar_url : meeting.requester.avatar_url) ? (
                          <img 
                            src={meeting.requester_id === currentUser.id ? meeting.recipient.avatar_url : meeting.requester.avatar_url} 
                            alt="Profile"
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-gray-600">
                            {(meeting.requester_id === currentUser.id ? meeting.recipient.name : meeting.requester.name)
                              .split(' ')
                              .map(n => n[0])
                              .join('')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons based on user role and meeting status */}
                    {meeting.status === 'pending' && (
                      <div className="absolute bottom-2 right-2 flex space-x-1">
                        {/* Only the recipient can accept/reject */}
                        {meeting.recipient_id === currentUser.id ? (
                          <>
                            <button
                              onClick={() => handleAcceptMeeting(meeting.id)}
                              className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-110"
                              title="Accept meeting"
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleRejectMeeting(meeting.id)}
                              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-110"
                              title="Reject meeting"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          /* Only the requester can cancel their own pending request */
                          <button
                            onClick={() => handleCancelMeeting(meeting.id)}
                            className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-110"
                            title="Cancel meeting request"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Cancel button for confirmed meetings - both parties can cancel */}
                    {meeting.status === 'confirmed' && (
                      <button
                        onClick={() => handleCancelMeeting(meeting.id)}
                        className="absolute bottom-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-110"
                        title="Cancel meeting"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No meetings scheduled for today</p>
              <p className="text-sm">Browse participants to schedule B2B meetings</p>
            </div>
          )}
        </div>
      </div>

      {/* Time Slots Overview */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Available Time Slots</h3>
          <p className="text-sm text-gray-600">B2B Meeting Sessions - 15 minute slots</p>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-4 gap-2">
            {MeetingService.generateAllTimeSlots().map((timeSlot) => {
              const isBooked = activeMeetings.some(m => m.time_slot === timeSlot);
              
              return (
                <div
                  key={timeSlot}
                  className={`p-2 text-center rounded-lg text-sm ${
                    isBooked
                      ? 'bg-yellow-100 font-medium'
                      : 'bg-gray-50 text-gray-600'
                  }`}
                  style={isBooked ? { color: '#C89116' } : {}}
                >
                  {timeSlot}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Custom Notification */}
      {notification.show && (
        <CustomNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default MyCalendar;